/**
 * Combobox parametrizable del modal "Agregar al inventario".
 *
 * Es el hermano de `fina-autocomplete` para los campos que necesitan desplegable REAL:
 * listado completo al abrir con la flecha, filtrado insensible a mayúsculas/acentos,
 * y dos capacidades opcionales que el autocomplete no tiene:
 * - `dataProvider`: modo remoto paginado (primera página al abrir, scroll al 80% pide
 *   la siguiente, búsqueda resuelta en servidor con debounce de 300 ms, reintento en fallo).
 * - `allowCreate`: ofrece la opción "Nueva: {texto}" cuando lo escrito no coincide
 *   exactamente con ninguna opción (para crear categorías inline).
 *
 * Se creó como componente aparte a propósito: `fina-autocomplete` tiene 12+ consumidores
 * y modificarlo arriesga regresiones. El patrón CVA/overlay/teclado es el mismo.
 *
 * Modos (mutuamente excluyentes):
 * - Estático: pasar `options` (lista completa). Filtra en cliente.
 * - Remoto: pasar `dataProvider(search, page)`. El componente no filtra: concatena páginas
 *   y delega la búsqueda al servidor.
 *
 * El valor del control (CVA) es SIEMPRE el string visible en el input, igual que el
 * autocomplete en modo lazy — así el payload de guardado no cambia (criterio A5).
 *
 * Ejemplo (categoría, estático + crear):
 *   <fina-combobox
 *       formControlName="category"
 *       [name]="'category'"
 *       [options]="categories"
 *       [optionMap]="{ label: 'cat', value: 'cat' }"
 *       [allowCreate]="true"
 *       [panelInOverlay]="true"
 *       (createOption)="onNewCategory($event)"
 *       (selectedItem)="onCategorySelected($event)"
 *   ></fina-combobox>
 *
 * Ejemplo (buscar items, remoto paginado):
 *   <fina-combobox
 *       [name]="'searchItems'"
 *       [dataProvider]="itemsProvider"
 *       [optionMap]="{ label: 'name', sublabel: 'SKU', value: 'name', id: '_id' }"
 *       [panelInOverlay]="true"
 *       (selectedItem)="onItemSelected($event)"
 *   ></fina-combobox>
 */

import { ConnectedPosition } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

export interface ComboboxOptionMap {
    /** Propiedad del item usada como texto principal de la opción. */
    label: string;
    /** Propiedad opcional mostrada como texto secundario (p. ej. SKU para distinguir homónimos). */
    sublabel?: string;
    /** Propiedad usada como valor del control; default: el label. */
    value?: string;
    /** Propiedad usada como id para dedupe entre páginas; default: el value. */
    id?: string;
}

export interface ComboboxOption {
    label: string;
    /** Label en minúsculas y sin diacríticos, precomputado una vez para no re-normalizar la lista completa en cada keystroke. */
    normalizedLabel: string;
    sublabel?: string;
    value: string;
    id: string;
    isCreateOption?: boolean;
}

export interface ComboboxPage {
    options: any[];
    hasMore: boolean;
}

export type ComboboxDataProvider = (search: string, page: number) => Observable<ComboboxPage>;

@Component({
    selector: 'fina-combobox',
    templateUrl: './combobox.component.html',
    styleUrls: ['combobox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ComboboxComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ComboboxComponent),
            multi: true,
        },
    ],
})
export class ComboboxComponent implements OnInit, OnChanges, ControlValueAccessor, Validator, OnDestroy {
    private static instanceCounter = 0;

    @Input() name!: string;
    @Input() label: string = '';
    @Input() labelSecondary: string = '';
    @Input() placeholder: string = '';
    @Input() disabled = false;
    @Input() required = false;
    @Input() requiredMessage = 'Este campo es obligatorio';
    /** Mapeo de propiedades para opciones OBJETO. Para listas de strings/números es opcional (se ignora). */
    @Input() optionMap: ComboboxOptionMap | null = null;
    /** Modo estático: lista completa de items; el filtrado es en cliente. */
    @Input() options: any[] = [];
    /** Modo remoto: función que resuelve una página; el filtrado es en servidor. */
    @Input() dataProvider: ComboboxDataProvider | null = null;
    /** Ofrecer "Nueva: {texto}" cuando lo escrito no coincide exactamente con una opción. */
    @Input() allowCreate = false;
    /**
     * Limpia el texto sin match exacto al cerrar el panel sin seleccionar (equivalente
     * al mode='select' del autocomplete): el campo solo puede quedar con una opción real.
     * Incompatible con allowCreate (que necesita texto libre).
     */
    @Input() enforceSelection = false;
    @Input() createOptionPrefix = 'Nueva';
    @Input() emptyMessage = 'No hay opciones disponibles';
    @Input() noResultsMessage = 'Sin resultados para esta búsqueda';
    /** Renderear el panel en un CDK Overlay para que no lo recorte el overflow del modal. */
    @Input() panelInOverlay = false;

    /** Item crudo seleccionado (el del rawList o la página remota). Para la opción "Nueva" no emite. */
    @Output() selectedItem = new EventEmitter<any>();
    /** Texto de la opción "Nueva: {texto}" al seleccionarla. */
    @Output() createOption = new EventEmitter<string>();

    @ViewChild('combobox') combobox!: ElementRef;
    @ViewChild('inputElementRef') inputElementRef!: ElementRef;

    inputFormControl: FormControl;

    isOptionsPanelVisible = false;
    highlightedIndex = -1;
    visibleOptions: ComboboxOption[] = [];
    loadingInitial = false;
    loadingMore = false;
    loadError = false;
    readonly optionsListId = `fina-combobox-options-${ComboboxComponent.instanceCounter++}`;

    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    ];

    private destroy$ = new Subject<void>();
    private staticOptions: ComboboxOption[] = [];
    private remoteOptions: ComboboxOption[] = [];
    private indexedItems = new Map<string, any>();
    private currentSearch = '';
    /**
     * true solo mientras el texto visible es un TÉRMINO TECLEADO por el usuario; false
     * tras seleccionar una opción o tras un writeValue externo (reset/precarga). Al abrir
     * el panel con filterActive=false se muestra el listado completo aunque el input tenga
     * texto — el valor seleccionado es una selección, no un filtro (criterios B8/C15).
     */
    private filterActive = false;
    private currentPage = 0;
    private hasMore = false;
    private remoteInitialized = false;
    /** Término (normalizado) de las páginas cargadas: si cambió por fuera (writeValue/selección), al reabrir se recarga. */
    private lastLoadedSearch = '';
    /** Descarta respuestas de requests viejos (p. ej. el usuario siguió escribiendo). */
    private requestSequence = 0;
    private onChange = (value: any) => {};
    private onTouched = () => {};
    /** Notifica al form padre que las reglas de validación cambiaron sin que cambiara el valor. */
    private onValidatorChange = () => {};

    /**
     * Cierra el panel cuando se scrollea FUERA del listado (p. ej. el body del modal).
     * Se registra en fase captura porque el scroll no burbujea y el ScrollDispatcher del
     * CDK solo ve window o contenedores cdkScrollable — el modal no lo es, así que las
     * scroll strategies (reposition/close) del overlay no se enteran y el panel quedaba
     * flotando despegado del input. El scroll INTERNO del listado (carga incremental)
     * no cierra el panel.
     */
    /** Momento de la última apertura del panel: da una ventana de gracia al scroll listener. */
    private panelOpenedAt = 0;

    private readonly externalScrollListener = (event: Event) => {
        if (!this.isOptionsPanelVisible) return;
        // Ventana de gracia: al reabrir por tecleo, el navegador puede auto-scrollear el
        // input a la vista (input al borde del modal, teclado móvil) en el mismo instante;
        // ese scroll no es del usuario y no debe cerrar el panel recién abierto.
        if (Date.now() - this.panelOpenedAt < 150) return;
        const target = event.target as HTMLElement | null;
        // El propio input dispara `scroll` cuando el texto tecleado excede el
        // ancho visible (desplaza su contenido horizontalmente) — eso NO es scroll de la
        // página/modal y no debe cerrar el panel, o la creación de categorías con nombres
        // largos queda inalcanzable. Se excluye todo el host del componente, no solo el panel.
        const hostElement = this.combobox?.nativeElement as HTMLElement | undefined;
        if (hostElement && target && hostElement.contains(target)) return;
        const panelElement = document.getElementById(this.optionsListId);
        if (panelElement && target && panelElement.contains(target)) return;
        this.closeOptionsPanel();
    };

    /** Término efectivo de filtrado/búsqueda: vacío cuando el texto visible no es un filtro. */
    private get effectiveSearch(): string {
        return this.filterActive ? this.currentSearch : '';
    }

    constructor(private cdr: ChangeDetectorRef) {}

    get isRemote(): boolean {
        return typeof this.dataProvider === 'function';
    }

    get isEmptyState(): boolean {
        return !this.loadingInitial && !this.loadError && this.visibleOptions.length === 0;
    }

    ngOnInit() {
        if (this.allowCreate && this.enforceSelection) {
            console.warn('fina-combobox: allowCreate y enforceSelection son mutuamente excluyentes. enforceSelection se ignora.');
        }
        this.readStaticOptions();
        this.configureInput();
    }

    ngOnDestroy() {
        this.isOptionsPanelVisible = false;
        window.removeEventListener('scroll', this.externalScrollListener, true);
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['options'] && !changes['options'].isFirstChange()) {
            this.readStaticOptions();
            this.refreshVisibleOptions();
        }

        if (changes['disabled'] && !changes['disabled'].isFirstChange() && this.inputFormControl) {
            if (this.disabled) this.inputFormControl.disable({ emitEvent: false });
            else this.inputFormControl.enable({ emitEvent: false });
        }

        // Si el required cambia después del init, se reconstruyen los validators internos y
        // se avisa al padre vía onValidatorChange para que re-ejecute validate() aunque el
        // valor no haya cambiado (Angular solo llama a validate() ante cambios de valor).
        if (changes['required'] && !changes['required'].isFirstChange() && this.inputFormControl) {
            this.inputFormControl.setValidators(this.required ? [Validators.required] : []);
            this.inputFormControl.updateValueAndValidity({ emitEvent: false });
            this.onValidatorChange();
        }
    }

    private configureInput() {
        const validators: ValidatorFn[] = this.required ? [Validators.required] : [];
        this.inputFormControl = new FormControl({ value: '', disabled: this.disabled }, validators);

        // onChange del CVA es INMEDIATO: el form padre no puede esperar el debounce
        // (un submit rápido leería el valor viejo). El debounce aplica solo a filtrado/búsqueda.
        // Estas emisiones SOLO provienen de tecleo real: selección, writeValue y
        // enforceSelection usan setValue con emitEvent:false y no pasan por acá.
        this.inputFormControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            this.onChange(value);
            // Tecleo = modo filtro, incondicional (enforceSelection lo necesita aunque el
            // usuario cierre antes de los 300 ms del debounce), y currentSearch se actualiza
            // acá para que apertura/creación lean siempre el texto vivo.
            this.currentSearch = (value ?? '').toString().trim();
            this.filterActive = true;
            // QA CRS-11 (corolario): teclear con el panel cerrado lo reabre — si el panel se
            // cerró (Escape, scroll del modal), seguir escribiendo recupera el listado y la
            // opción "Nueva: {texto}"; antes quedaba cerrado para siempre.
            if (!this.isOptionsPanelVisible) this.openOptionsPanel();
        });

        this.inputFormControl.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => {
            // Guard anti-carrera: si al vencer el debounce el control ya tiene otro valor
            // (el usuario seleccionó una opción o el padre reseteó el control con
            // emitEvent:false), esta emisión es vieja y se descarta. currentSearch y
            // filterActive ya se actualizaron en el handler inmediato — acá solo se
            // dispara el filtrado/búsqueda, que es lo único que debe esperar el debounce.
            if (value !== this.inputFormControl.value) return;
            if (this.isRemote) {
                // Si la reapertura por tecleo ya cargó este mismo término, no se repite la
                // request (evita el doble HTTP y el flash lista→spinner→lista); con loadError
                // sí se reintenta.
                const alreadyLoaded = this.normalize(this.effectiveSearch) === this.lastLoadedSearch && !this.loadError;
                if (this.isOptionsPanelVisible && !alreadyLoaded) this.loadRemotePage(1, true);
            } else {
                this.refreshVisibleOptions();
            }
            this.cdr.markForCheck();
        });
    }

    // ------------------------------------------------------------------ estático

    private readStaticOptions() {
        this.indexedItems.clear();
        this.staticOptions = (this.options ?? []).map(item => this.buildOption(item)).filter((option): option is ComboboxOption => option !== null);
    }

    private buildOption(item: any): ComboboxOption | null {
        if (item === null || item === undefined) return null;

        if (typeof item === 'string' || typeof item === 'number') {
            const label = item.toString().trim();
            if (label === '') return null;
            const option: ComboboxOption = { label, normalizedLabel: this.normalize(label), value: label, id: label };
            this.indexedItems.set(option.id, item);
            return option;
        }

        // El optionMap solo es obligatorio cuando las opciones son objetos; el throw vive acá
        // (y no en ngOnInit) para que las listas de strings no exijan un mapeo que se ignora.
        if (!this.optionMap) {
            throw new Error('fina-combobox: optionMap es requerido cuando las opciones son objetos');
        }

        const label = (item[this.optionMap.label] ?? '').toString();
        if (label.trim() === '') return null; // datos sucios (p. ej. proveedores null en cuentas por pagar)
        const value = (this.optionMap.value ? item[this.optionMap.value] : label)?.toString() ?? label;
        const id = (this.optionMap.id ? item[this.optionMap.id] : value)?.toString() ?? value;
        const rawSublabel = this.optionMap.sublabel ? item[this.optionMap.sublabel] : null;
        const sublabel = rawSublabel !== null && rawSublabel !== undefined && rawSublabel.toString().trim() !== '' ? rawSublabel.toString() : undefined;

        const option: ComboboxOption = { label, normalizedLabel: this.normalize(label), value, id, sublabel };
        this.indexedItems.set(option.id, item);
        return option;
    }

    /** Minúsculas y sin diacríticos: "Lácteos" matchea "lacteos". */
    private normalize(text: string): string {
        return (text ?? '').toString().trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    private refreshVisibleOptions() {
        let baseList: ComboboxOption[];

        if (this.isRemote) {
            baseList = [...this.remoteOptions];
        } else {
            const term = this.normalize(this.effectiveSearch);
            baseList = term === '' ? [...this.staticOptions] : this.staticOptions.filter(option => option.normalizedLabel.includes(term));
        }

        const createOpt = this.buildCreateOption();
        this.visibleOptions = createOpt ? [createOpt, ...baseList] : baseList;
        this.ensureHighlightInRange();
    }

    private buildCreateOption(): ComboboxOption | null {
        if (!this.allowCreate || !this.filterActive) return null;
        const text = this.currentSearch.trim();
        if (text === '') return null;

        const normalizedText = this.normalize(text);
        const sourceList = this.isRemote ? this.remoteOptions : this.staticOptions;
        const exactMatch = sourceList.some(option => option.normalizedLabel === normalizedText);
        if (exactMatch) return null;

        return { label: text, normalizedLabel: normalizedText, value: text, id: `__create__${text}`, isCreateOption: true };
    }

    // ------------------------------------------------------------------ remoto

    private loadRemotePage(page: number, reset: boolean) {
        if (!this.isRemote) return;
        if (!reset && (this.loadingMore || !this.hasMore)) return;

        const requestId = ++this.requestSequence;
        if (reset) {
            this.loadingInitial = true;
            this.remoteOptions = [];
            this.refreshVisibleOptions();
        } else {
            this.loadingMore = true;
        }
        this.loadError = false;
        this.cdr.markForCheck();

        this.dataProvider(this.effectiveSearch, page)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: pageResult => {
                    if (requestId !== this.requestSequence) return; // respuesta vieja: el usuario siguió escribiendo
                    const newOptions = (pageResult?.options ?? []).map(item => this.buildOption(item)).filter((option): option is ComboboxOption => option !== null);
                    const knownIds = new Set(this.remoteOptions.map(option => option.id));
                    this.remoteOptions = reset ? newOptions : [...this.remoteOptions, ...newOptions.filter(option => !knownIds.has(option.id))];
                    this.currentPage = page;
                    this.hasMore = !!pageResult?.hasMore;
                    this.remoteInitialized = true;
                    this.lastLoadedSearch = this.normalize(this.effectiveSearch);
                    this.loadingInitial = false;
                    this.loadingMore = false;
                    this.refreshVisibleOptions();
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (requestId !== this.requestSequence) return;
                    // No se pierde lo ya cargado: solo se muestra la fila de reintento al final.
                    this.loadingInitial = false;
                    this.loadingMore = false;
                    this.loadError = true;
                    this.cdr.markForCheck();
                },
            });
    }

    retryLoad(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        const nextPage = this.remoteOptions.length === 0 ? 1 : this.currentPage + 1;
        this.loadRemotePage(nextPage, this.remoteOptions.length === 0);
    }

    onPanelScroll(event: Event) {
        if (!this.isRemote || this.loadingMore || this.loadingInitial || !this.hasMore || this.loadError) return;
        const panel = event.target as HTMLElement;
        const scrolled = panel.scrollTop + panel.clientHeight;
        if (scrolled >= panel.scrollHeight * 0.8) {
            this.loadRemotePage(this.currentPage + 1, false);
        }
    }

    // ------------------------------------------------------------------ apertura / selección / teclado

    handleInputFocus(): void {
        this.openOptionsPanel();
    }

    handleInputBlur(): void {
        this.onTouched();
    }

    handleArrowClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled) return;

        if (this.isOptionsPanelVisible) {
            this.closeOptionsPanel();
        } else {
            this.openOptionsPanel(true);
        }
    }

    handleInputKeydown(event: KeyboardEvent): void {
        if (this.disabled) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.openOptionsPanel();
                this.moveHighlight(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.openOptionsPanel();
                this.moveHighlight(-1);
                break;
            case 'Enter':
                if (this.isOptionsPanelVisible && this.highlightedIndex >= 0) {
                    event.preventDefault();
                    const highlighted = this.visibleOptions[this.highlightedIndex];
                    if (highlighted) this.onOptionSelected(highlighted);
                }
                break;
            case 'Escape':
                if (this.isOptionsPanelVisible) {
                    event.preventDefault();
                    this.closeOptionsPanel();
                }
                break;
            case 'Tab':
                this.closeOptionsPanel();
                break;
        }
    }

    onOptionSelected(option: ComboboxOption): void {
        if (!option) return;

        // emitEvent:false a propósito: el onChange se dispara acá de forma síncrona, y la
        // emisión que pudiera quedar en vuelo en el debounce muere en el guard anti-carrera.
        this.inputFormControl.setValue(option.value, { emitEvent: false });
        this.inputFormControl.markAsDirty();
        this.currentSearch = option.value;
        // El texto queda como SELECCIÓN, no como filtro: al reabrir se ve el listado completo.
        this.filterActive = false;
        this.onChange(option.value);
        this.onTouched();

        if (option.isCreateOption) {
            this.createOption.emit(option.value);
        } else {
            this.selectedItem.emit(this.indexedItems.get(option.id));
        }

        this.closeOptionsPanel();
    }

    private openOptionsPanel(focusInput: boolean = false): void {
        if (this.disabled) return;

        if (!this.isOptionsPanelVisible) {
            this.isOptionsPanelVisible = true;
            this.panelOpenedAt = Date.now();
            // Recargar si nunca se cargó o si el término efectivo ya no corresponde a las
            // páginas cargadas (p. ej. se limpió el input al seleccionar un item, o el texto
            // visible es una selección y las páginas cargadas eran de una búsqueda).
            if (this.isRemote && (!this.remoteInitialized || this.normalize(this.effectiveSearch) !== this.lastLoadedSearch)) {
                this.loadRemotePage(1, true);
            } else {
                this.refreshVisibleOptions();
            }
            this.setInitialHighlight();
            window.addEventListener('scroll', this.externalScrollListener, true);
        }

        if (focusInput) this.inputElementRef?.nativeElement.focus();
        this.cdr.markForCheck();
    }

    private closeOptionsPanel(): void {
        window.removeEventListener('scroll', this.externalScrollListener, true);
        this.enforceSelectionOnClose();
        this.isOptionsPanelVisible = false;
        this.highlightedIndex = -1;
        this.cdr.markForCheck();
    }

    /**
     * Con enforceSelection, texto tecleado sin match exacto se limpia al cerrar el panel
     * (equivalente al ensureValidSelection del autocomplete en mode='select'). Solo actúa
     * sobre texto en modo filtro: una selección previa (filterActive=false) nunca se borra.
     */
    private enforceSelectionOnClose(): void {
        // allowCreate necesita texto libre: si ambos flags vienen en true, gana allowCreate
        // (ver el warn de ngOnInit) y acá no se borra nada.
        // Con una carga en vuelo (inicial O de página siguiente) o fallida, la lista está
        // vacía/incompleta: no hay datos para juzgar el match exacto y borrar el texto
        // sería un falso negativo (p. ej. teclear "Harina" válida y cerrar antes de la respuesta).
        if (!this.enforceSelection || this.allowCreate || !this.filterActive || this.loadingInitial || this.loadingMore || this.loadError) return;
        // En remoto solo se puede juzgar el match si las páginas cargadas corresponden al
        // término ACTUAL: un cierre antes del debounce dejaría remoteOptions con el término
        // anterior y borraría texto que sí existe en el catálogo (falso negativo).
        if (this.isRemote && this.normalize(this.currentSearch) !== this.lastLoadedSearch) return;
        const text = (this.inputFormControl?.value ?? '').toString().trim();
        if (text === '') return;

        const normalizedText = this.normalize(text);
        const sourceList = this.isRemote ? this.remoteOptions : this.staticOptions;
        const exactMatch = sourceList.some(option => option.normalizedLabel === normalizedText);
        if (exactMatch) return;

        this.inputFormControl.setValue('', { emitEvent: false });
        this.currentSearch = '';
        this.filterActive = false;
        this.onChange('');
    }

    private setInitialHighlight(): void {
        this.highlightedIndex = this.visibleOptions.length > 0 ? 0 : -1;
    }

    private ensureHighlightInRange(): void {
        if (this.visibleOptions.length === 0) {
            this.highlightedIndex = -1;
        } else if (this.highlightedIndex >= this.visibleOptions.length) {
            this.highlightedIndex = this.visibleOptions.length - 1;
        }
    }

    private moveHighlight(step: number): void {
        if (this.visibleOptions.length === 0) {
            this.highlightedIndex = -1;
            return;
        }
        const currentIndex = this.highlightedIndex === -1 ? (step > 0 ? -1 : 0) : this.highlightedIndex;
        this.highlightedIndex = (currentIndex + step + this.visibleOptions.length) % this.visibleOptions.length;
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        if (!this.isOptionsPanelVisible || !this.combobox) return;

        const target = event.target as HTMLElement;
        const clickedInsideInput = this.combobox.nativeElement.contains(target);
        // El panel puede estar portaleado al body (overlay): se identifica por su id.
        const panelElement = document.getElementById(this.optionsListId);
        const clickedInsidePanel = !!panelElement && panelElement.contains(target);

        if (!clickedInsideInput && !clickedInsidePanel) {
            this.closeOptionsPanel();
        }
    }

    // ------------------------------------------------------------------ CVA / Validator

    writeValue(value: string): void {
        // null/undefined = reset del padre: se limpia el texto visible (antes se ignoraba y el
        // input quedaba mostrando un valor que el form ya no tenía).
        const next = value === null || value === undefined ? '' : value;
        this.inputFormControl?.setValue(next, { emitEvent: false });
        this.currentSearch = next.toString().trim();
        // Valor escrito desde afuera = selección/precarga, no un filtro del usuario.
        this.filterActive = false;
        this.cdr.markForCheck();
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (!this.inputFormControl) return;
        if (isDisabled) this.inputFormControl.disable({ emitEvent: false });
        else this.inputFormControl.enable({ emitEvent: false });
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.inputFormControl?.errors;
    }
}
